import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import '../assets/styles/SEODashboard.css';

interface SeoMetadata {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  structuredData: {
    name: string;
    description: string;
    applicationCategory: string;
    operatingSystem: string;
  };
}

const SEODashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('general');
  const [previewDevice, setPreviewDevice] = useState<string>('desktop');
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<string>('');
  
  // Default SEO metadata
  const [seoMetadata, setSeoMetadata] = useState<SeoMetadata>({
    title: "Price on Rails | AI-Powered Trading Autocomplete | Translate Stocks",
    description: "Experience Price on Rails - the revolutionary AI autocomplete for intraday charts. Make faster, more informed trading decisions with our predictive analytics platform.",
    keywords: "trading platform, AI trading, predictive analytics, stock market, day trading, automated trading, Price on Rails, Translate Stocks",
    canonicalUrl: "https://translatestocks.com/price-on-rails",
    ogTitle: "Price on Rails | Next-Gen Trading Platform",
    ogDescription: "Predict market moves with AI-powered autocomplete for charts. Make faster, more profitable trading decisions.",
    ogImage: "https://translatestocks.com/images/price-on-rails-og.jpg",
    twitterTitle: "Price on Rails | Next-Gen Trading Platform",
    twitterDescription: "Predict market moves with AI-powered autocomplete for charts. Make faster, more profitable trading decisions.",
    twitterImage: "https://translatestocks.com/images/price-on-rails-twitter.jpg",
    structuredData: {
      name: "Price on Rails",
      description: "AI-powered trading platform with predictive analytics for stocks, futures, and crypto markets.",
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web-based"
    }
  });

  // Campaign tracking data
  const [campaignData, setCampaignData] = useState({
    utmSource: "google",
    utmMedium: "cpc",
    utmCampaign: "price_on_rails_launch",
    utmContent: "predictive_trading",
    utmTerm: "ai_trading_platform",
    gclid: "{gclid}"
  });

  const [keywordPerformance, setKeywordPerformance] = useState([
    { keyword: "ai trading platform", clicks: 2450, impressions: 28500, ctr: 8.6, position: 2.3, cpc: 1.75 },
    { keyword: "predictive stock analysis", clicks: 1820, impressions: 19200, ctr: 9.5, position: 1.8, cpc: 2.10 },
    { keyword: "automated trading software", clicks: 1650, impressions: 22800, ctr: 7.2, position: 3.1, cpc: 1.95 },
    { keyword: "trading signals software", clicks: 1320, impressions: 15600, ctr: 8.5, position: 2.6, cpc: 1.85 },
    { keyword: "best ai for day trading", clicks: 980, impressions: 12400, ctr: 7.9, position: 3.4, cpc: 2.35 }
  ]);

  // Simulate loading data from an API
  useEffect(() => {
    // This would typically be an API call
    const loadSavedData = () => {
      // Simulating API delay
      setTimeout(() => {
        // Data would be loaded from backend
        setIsDirty(false);
      }, 500);
    };
    
    loadSavedData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, section: string) => {
    const { name, value } = e.target;
    
    if (section === 'main') {
      setSeoMetadata({
        ...seoMetadata,
        [name]: value
      });
    } else if (section === 'structuredData') {
      setSeoMetadata({
        ...seoMetadata,
        structuredData: {
          ...seoMetadata.structuredData,
          [name]: value
        }
      });
    } else if (section === 'campaign') {
      setCampaignData({
        ...campaignData,
        [name]: value
      });
    }
    
    setIsDirty(true);
  };

  const handleSaveChanges = () => {
    // This would typically save to an API
    setSaveStatus('saving');
    
    // Simulate API call
    setTimeout(() => {
      setSaveStatus('saved');
      setIsDirty(false);
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setSaveStatus('');
      }, 3000);
    }, 1000);
  };

  const generateCampaignUrl = () => {
    const baseUrl = seoMetadata.canonicalUrl;
    const params = new URLSearchParams();
    
    Object.entries(campaignData).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    return `${baseUrl}?${params.toString()}`;
  };

  // SEO score calculation (simplified)
  const calculateSeoScore = () => {
    let score = 0;
    
    // Title length check (50-60 chars is optimal)
    const titleLength = seoMetadata.title.length;
    if (titleLength >= 50 && titleLength <= 60) score += 20;
    else if (titleLength >= 40 && titleLength <= 70) score += 15;
    else score += 10;
    
    // Description length check (150-160 chars is optimal)
    const descLength = seoMetadata.description.length;
    if (descLength >= 150 && descLength <= 160) score += 20;
    else if (descLength >= 130 && descLength <= 170) score += 15;
    else score += 10;
    
    // Keywords check
    if (seoMetadata.keywords.split(',').length >= 5) score += 20;
    else score += 10;
    
    // OG tags check
    if (seoMetadata.ogTitle && seoMetadata.ogDescription && seoMetadata.ogImage) score += 20;
    else score += 10;
    
    // Structured data check
    if (Object.values(seoMetadata.structuredData).every(val => val !== '')) score += 20;
    else score += 10;
    
    return score;
  };

  const seoScore = calculateSeoScore();

  return (
    <div className="seo-dashboard">
      <Helmet>
        <title>SEO Dashboard | Price on Rails</title>
      </Helmet>
      
      <header className="dashboard-header">
        <h1>SEO & Campaign Dashboard</h1>
        <div className="dashboard-actions">
          <button 
            className={`save-button ${isDirty ? 'active' : ''}`}
            onClick={handleSaveChanges}
            disabled={!isDirty || saveStatus === 'saving'}
          >
            {saveStatus === 'saving' ? 'Saving...' : 
             saveStatus === 'saved' ? 'Saved!' : 'Save Changes'}
          </button>
          <button
            className="preview-button"
            onClick={() => setIsPreviewOpen(!isPreviewOpen)}
          >
            {isPreviewOpen ? 'Close Preview' : 'Preview Page'}
          </button>
        </div>
      </header>
      
      <div className="dashboard-content">
        <div className="dashboard-sidebar">
          <div className="seo-score-card">
            <div className="score-circle" style={{ 
              background: `conic-gradient(#4CAF50 ${seoScore}%, #f3f3f3 0)`
            }}>
              <span className="score-value">{seoScore}</span>
            </div>
            <h3>SEO Score</h3>
            <p>{seoScore >= 80 ? 'Excellent' : seoScore >= 60 ? 'Good' : 'Needs Improvement'}</p>
          </div>
          
          <nav className="dashboard-nav">
            <button
              className={activeTab === 'general' ? 'active' : ''}
              onClick={() => setActiveTab('general')}
            >
              General SEO
            </button>
            <button
              className={activeTab === 'social' ? 'active' : ''}
              onClick={() => setActiveTab('social')}
            >
              Social Media
            </button>
            <button
              className={activeTab === 'structured' ? 'active' : ''}
              onClick={() => setActiveTab('structured')}
            >
              Structured Data
            </button>
            <button
              className={activeTab === 'campaigns' ? 'active' : ''}
              onClick={() => setActiveTab('campaigns')}
            >
              PPC Campaigns
            </button>
            <button
              className={activeTab === 'keywords' ? 'active' : ''}
              onClick={() => setActiveTab('keywords')}
            >
              Keyword Analysis
            </button>
          </nav>
        </div>
        
        <div className="dashboard-main">
          {activeTab === 'general' && (
            <div className="tab-content">
              <h2>General SEO Settings</h2>
              
              <div className="form-group">
                <label htmlFor="title">Page Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={seoMetadata.title}
                  onChange={(e) => handleInputChange(e, 'main')}
                  maxLength={70}
                />
                <div className="character-counter">
                  <span className={seoMetadata.title.length > 60 ? 'warning' : ''}>
                    {seoMetadata.title.length}
                  </span>/70 
                  {seoMetadata.title.length > 60 && <span> (Recommended: 50-60)</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Meta Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={seoMetadata.description}
                  onChange={(e) => handleInputChange(e, 'main')}
                  maxLength={170}
                  rows={3}
                ></textarea>
                <div className="character-counter">
                  <span className={seoMetadata.description.length < 130 || seoMetadata.description.length > 160 ? 'warning' : ''}>
                    {seoMetadata.description.length}
                  </span>/170 
                  {(seoMetadata.description.length < 130 || seoMetadata.description.length > 160) && 
                    <span> (Recommended: 150-160)</span>
                  }
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="keywords">Keywords</label>
                <input
                  type="text"
                  id="keywords"
                  name="keywords"
                  value={seoMetadata.keywords}
                  onChange={(e) => handleInputChange(e, 'main')}
                />
                <div className="keyword-hint">Separate keywords with commas</div>
              </div>
              
              <div className="form-group">
                <label htmlFor="canonicalUrl">Canonical URL</label>
                <input
                  type="url"
                  id="canonicalUrl"
                  name="canonicalUrl"
                  value={seoMetadata.canonicalUrl}
                  onChange={(e) => handleInputChange(e, 'main')}
                />
              </div>
            </div>
          )}
          
          {activeTab === 'social' && (
            <div className="tab-content">
              <h2>Social Media Preview</h2>
              
              <div className="social-tabs">
                <button 
                  className={previewDevice === 'facebook' ? 'active' : ''}
                  onClick={() => setPreviewDevice('facebook')}
                >
                  Facebook
                </button>
                <button 
                  className={previewDevice === 'twitter' ? 'active' : ''}
                  onClick={() => setPreviewDevice('twitter')}
                >
                  Twitter
                </button>
                <button 
                  className={previewDevice === 'linkedin' ? 'active' : ''}
                  onClick={() => setPreviewDevice('linkedin')}
                >
                  LinkedIn
                </button>
              </div>
              
              <div className="social-preview">
                {previewDevice === 'facebook' && (
                  <div className="facebook-preview">
                    <div className="preview-image">
                      <img src={seoMetadata.ogImage || '/images/placeholder.jpg'} alt="Social preview" />
                    </div>
                    <div className="preview-content">
                      <div className="preview-site">translatestocks.com</div>
                      <div className="preview-title">{seoMetadata.ogTitle}</div>
                      <div className="preview-description">{seoMetadata.ogDescription}</div>
                    </div>
                  </div>
                )}
                
                {previewDevice === 'twitter' && (
                  <div className="twitter-preview">
                    <div className="preview-image">
                      <img src={seoMetadata.twitterImage || '/images/placeholder.jpg'} alt="Social preview" />
                    </div>
                    <div className="preview-content">
                      <div className="preview-site">translatestocks.com</div>
                      <div className="preview-title">{seoMetadata.twitterTitle}</div>
                      <div className="preview-description">{seoMetadata.twitterDescription}</div>
                    </div>
                  </div>
                )}
                
                {previewDevice === 'linkedin' && (
                  <div className="linkedin-preview">
                    <div className="preview-image">
                      <img src={seoMetadata.ogImage || '/images/placeholder.jpg'} alt="Social preview" />
                    </div>
                    <div className="preview-content">
                      <div className="preview-site">translatestocks.com</div>
                      <div className="preview-title">{seoMetadata.ogTitle}</div>
                      <div className="preview-description">{seoMetadata.ogDescription}</div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="form-columns">
                <div className="form-column">
                  <h3>Open Graph (Facebook/LinkedIn)</h3>
                  
                  <div className="form-group">
                    <label htmlFor="ogTitle">OG Title</label>
                    <input
                      type="text"
                      id="ogTitle"
                      name="ogTitle"
                      value={seoMetadata.ogTitle}
                      onChange={(e) => handleInputChange(e, 'main')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="ogDescription">OG Description</label>
                    <textarea
                      id="ogDescription"
                      name="ogDescription"
                      value={seoMetadata.ogDescription}
                      onChange={(e) => handleInputChange(e, 'main')}
                      rows={3}
                    ></textarea>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="ogImage">OG Image URL</label>
                    <input
                      type="url"
                      id="ogImage"
                      name="ogImage"
                      value={seoMetadata.ogImage}
                      onChange={(e) => handleInputChange(e, 'main')}
                    />
                  </div>
                </div>
                
                <div className="form-column">
                  <h3>Twitter Card</h3>
                  
                  <div className="form-group">
                    <label htmlFor="twitterTitle">Twitter Title</label>
                    <input
                      type="text"
                      id="twitterTitle"
                      name="twitterTitle"
                      value={seoMetadata.twitterTitle}
                      onChange={(e) => handleInputChange(e, 'main')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="twitterDescription">Twitter Description</label>
                    <textarea
                      id="twitterDescription"
                      name="twitterDescription"
                      value={seoMetadata.twitterDescription}
                      onChange={(e) => handleInputChange(e, 'main')}
                      rows={3}
                    ></textarea>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="twitterImage">Twitter Image URL</label>
                    <input
                      type="url"
                      id="twitterImage"
                      name="twitterImage"
                      value={seoMetadata.twitterImage}
                      onChange={(e) => handleInputChange(e, 'main')}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'structured' && (
            <div className="tab-content">
              <h2>Structured Data</h2>
              <p className="info-text">
                Structured data helps search engines understand the content of your page 
                and can be used to display rich snippets in search results.
              </p>
              
              <div className="form-group">
                <label htmlFor="name">Application Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={seoMetadata.structuredData.name}
                  onChange={(e) => handleInputChange(e, 'structuredData')}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="applicationCategory">Application Category</label>
                <input
                  type="text"
                  id="applicationCategory"
                  name="applicationCategory"
                  value={seoMetadata.structuredData.applicationCategory}
                  onChange={(e) => handleInputChange(e, 'structuredData')}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Application Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={seoMetadata.structuredData.description}
                  onChange={(e) => handleInputChange(e, 'structuredData')}
                  rows={3}
                ></textarea>
              </div>
              
              <div className="form-group">
                <label htmlFor="operatingSystem">Operating System</label>
                <input
                  type="text"
                  id="operatingSystem"
                  name="operatingSystem"
                  value={seoMetadata.structuredData.operatingSystem}
                  onChange={(e) => handleInputChange(e, 'structuredData')}
                />
              </div>
              
              <div className="structured-data-preview">
                <h3>JSON-LD Preview</h3>
                <pre>
{`{
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "${seoMetadata.structuredData.name}",
  "applicationCategory": "${seoMetadata.structuredData.applicationCategory}",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "operatingSystem": "${seoMetadata.structuredData.operatingSystem}",
  "description": "${seoMetadata.structuredData.description}"
}`}
                </pre>
              </div>
            </div>
          )}
          
          {activeTab === 'campaigns' && (
            <div className="tab-content">
              <h2>PPC Campaign Configuration</h2>
              <p className="info-text">
                Configure UTM parameters for your PPC campaigns to track traffic and conversions effectively.
              </p>
              
              <div className="form-columns">
                <div className="form-column">
                  <div className="form-group">
                    <label htmlFor="utmSource">UTM Source</label>
                    <input
                      type="text"
                      id="utmSource"
                      name="utmSource"
                      value={campaignData.utmSource}
                      onChange={(e) => handleInputChange(e, 'campaign')}
                      placeholder="e.g., google, facebook"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="utmMedium">UTM Medium</label>
                    <input
                      type="text"
                      id="utmMedium"
                      name="utmMedium"
                      value={campaignData.utmMedium}
                      onChange={(e) => handleInputChange(e, 'campaign')}
                      placeholder="e.g., cpc, banner, email"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="utmCampaign">UTM Campaign</label>
                    <input
                      type="text"
                      id="utmCampaign"
                      name="utmCampaign"
                      value={campaignData.utmCampaign}
                      onChange={(e) => handleInputChange(e, 'campaign')}
                      placeholder="e.g., spring_sale"
                    />
                  </div>
                </div>
                
                <div className="form-column">
                  <div className="form-group">
                    <label htmlFor="utmContent">UTM Content</label>
                    <input
                      type="text"
                      id="utmContent"
                      name="utmContent"
                      value={campaignData.utmContent}
                      onChange={(e) => handleInputChange(e, 'campaign')}
                      placeholder="e.g., logo_link, text_link"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="utmTerm">UTM Term</label>
                    <input
                      type="text"
                      id="utmTerm"
                      name="utmTerm"
                      value={campaignData.utmTerm}
                      onChange={(e) => handleInputChange(e, 'campaign')}
                      placeholder="e.g., running+shoes"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="gclid">GCLID Parameter</label>
                    <input
                      type="text"
                      id="gclid"
                      name="gclid"
                      value={campaignData.gclid}
                      onChange={(e) => handleInputChange(e, 'campaign')}
                      placeholder="{gclid}"
                      disabled
                    />
                    <div className="input-hint">This parameter is automatically added by Google Ads</div>
                  </div>
                </div>
              </div>
              
              <div className="campaign-url-preview">
                <h3>Generated Campaign URL</h3>
                <div className="url-display">
                  {generateCampaignUrl()}
                </div>
                <button 
                  className="copy-button"
                  onClick={() => navigator.clipboard.writeText(generateCampaignUrl())}
                >
                  Copy URL
                </button>
              </div>
              
              <div className="campaign-templates">
                <h3>Campaign Templates</h3>
                <div className="template-buttons">
                  <button onClick={() => {
                    setCampaignData({
                      utmSource: "google",
                      utmMedium: "cpc",
                      utmCampaign: "price_on_rails_launch",
                      utmContent: "predictive_trading",
                      utmTerm: "ai_trading_platform",
                      gclid: "{gclid}"
                    });
                    setIsDirty(true);
                  }}>
                    Google Ads Template
                  </button>
                  <button onClick={() => {
                    setCampaignData({
                      utmSource: "facebook",
                      utmMedium: "social",
                      utmCampaign: "price_on_rails_launch",
                      utmContent: "carousel_ad",
                      utmTerm: "",
                      gclid: ""
                    });
                    setIsDirty(true);
                  }}>
                    Facebook Ads Template
                  </button>
                  <button onClick={() => {
                    setCampaignData({
                      utmSource: "linkedin",
                      utmMedium: "social",
                      utmCampaign: "trader_professionals",
                      utmContent: "sponsored_post",
                      utmTerm: "",
                      gclid: ""
                    });
                    setIsDirty(true);
                  }}>
                    LinkedIn Ads Template
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'keywords' && (
            <div className="tab-content">
              <h2>Keyword Performance Analysis</h2>
              <p className="info-text">
                Analyze the performance of keywords driving traffic to your landing page.
              </p>
              
              <div className="keywords-table">
                <table>
                  <thead>
                    <tr>
                      <th>Keyword</th>
                      <th>Clicks</th>
                      <th>Impressions</th>
                      <th>CTR (%)</th>
                      <th>Avg. Position</th>
                      <th>CPC ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywordPerformance.map((keyword, index) => (
                      <tr key={index}>
                        <td>{keyword.keyword}</td>
                        <td>{keyword.clicks.toLocaleString()}</td>
                        <td>{keyword.impressions.toLocaleString()}</td>
                        <td>{keyword.ctr.toFixed(1)}%</td>
                        <td>{keyword.position.toFixed(1)}</td>
                        <td>${keyword.cpc.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="keyword-suggestions">
                <h3>Recommended Keywords</h3>
                <div className="suggestion-tags">
                  <span className="keyword-tag">predictive trading tools</span>
                  <span className="keyword-tag">ai market predictions</span>
                  <span className="keyword-tag">trading forecast software</span>
                  <span className="keyword-tag">auto trading bot</span>
                  <span className="keyword-tag">algorithmic trading platform</span>
                  <span className="keyword-tag">chart pattern predictor</span>
                  <span className="keyword-tag">machine learning stock predictions</span>
                  <span className="keyword-tag">best ai trading assistant</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {isPreviewOpen && (
        <div className="page-preview">
          <div className="preview-header">
            <h3>Page Preview</h3>
            <div className="preview-device-selector">
              <button 
                className={previewDevice === 'desktop' ? 'active' : ''}
                onClick={() => setPreviewDevice('desktop')}
              >
                Desktop
              </button>
              <button 
                className={previewDevice === 'tablet' ? 'active' : ''}
                onClick={() => setPreviewDevice('tablet')}
              >
                Tablet
              </button>
              <button 
                className={previewDevice === 'mobile' ? 'active' : ''}
                onClick={() => setPreviewDevice('mobile')}
              >
                Mobile
              </button>
            </div>
            <button 
              className="close-preview"
              onClick={() => setIsPreviewOpen(false)}
            >
              ✕
            </button>
          </div>
          
          <div className={`preview-container ${previewDevice}`}>
            <div className="preview-frame">
              <div className="preview-browser-chrome">
                <div className="preview-browser-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="preview-browser-address">
                  {seoMetadata.canonicalUrl}
                </div>
              </div>
              <div className="preview-content">
                <img 
                  src={`/images/preview-${previewDevice}.jpg`} 
                  alt="Page preview"
                  className="preview-image"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEODashboard;
